import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Logo from "./Logo";

/*
 * 1-second premium intro shown once per session on homepage first visit.
 * - Black background
 * - Liftile logo fades in
 * - Orange light sweep across the logo
 * - "RISE WITH CONFIDENCE" tagline appears
 * - Wipes away
 */
export default function LoadingScreen({ onDone }) {
  const [exiting, setExiting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setExiting(true), 1000);
    const t2 = setTimeout(() => {
      setDone(true);
      onDone && onDone();
    }, 1500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (done) return null;

  return (
    <motion.div
      data-testid="loading-screen"
      initial={{ y: 0 }}
      animate={{ y: exiting ? "-100%" : 0 }}
      transition={{ duration: 0.5, ease: [0.7, 0, 0.3, 1] }}
      className="fixed inset-0 z-[100] bg-liftile-black overflow-hidden pointer-events-none flex items-center justify-center"
    >
      {/* Subtle industrial glow */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(circle at 50% 50%, rgba(255,107,26,0.10), transparent 55%)" }}
      />

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
        className="relative flex flex-col items-center"
      >
        {/* Logo with light sweep masked over top */}
        <div className="relative">
          <Logo variant="mark" theme="light" className="h-16 md:h-20 lg:h-24 w-auto" />
          {/* Orange light sweep — animated across the logo */}
          <motion.div
            aria-hidden
            initial={{ x: "-110%" }}
            animate={{ x: "110%" }}
            transition={{ duration: 0.9, ease: "easeOut", delay: 0.15 }}
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(105deg, transparent 35%, rgba(255,107,26,0.55) 50%, transparent 65%)",
              mixBlendMode: "screen",
              filter: "blur(4px)",
            }}
          />
        </div>

        <motion.div
          initial={{ opacity: 0, letterSpacing: "0.2em" }}
          animate={{ opacity: 1, letterSpacing: "0.4em" }}
          transition={{ delay: 0.35, duration: 0.55 }}
          className="font-heading text-[10px] md:text-xs uppercase text-liftile-cream/80 mt-5"
        >
          Rise With Confidence
        </motion.div>
      </motion.div>
    </motion.div>
  );
}
